import React from 'react';
import {
  Box,
  Button,
  Dialog,
  IconButton,
  InputBase,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import type { SelectChangeEvent } from '@mui/material/Select';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';

type MealSlotDialogMode = 'add' | 'edit';
type Meridiem = 'AM' | 'PM';
type MealTypeOption = { value: string; label: string };

export type EditMealSlotDialogProps = {
  open: boolean;
  mode: MealSlotDialogMode;
  isSaving: boolean;
  mealName: string;
  mealType?: string;
  canEditMealType?: boolean;
  mealTypeOptions?: MealTypeOption[];
  mealTime: string;
  targetKcal: string;
  onMealNameChange: (value: string) => void;
  onMealTypeChange?: (value: string) => void;
  onMealTimeChange: (value: string) => void;
  onTargetKcalChange: (value: string) => void;
  onClose: () => void;
  onSave: () => void;
};

const clamp = (value: number, min: number, max: number) => Math.max(min, Math.min(max, value));
const pad2 = (value: number) => String(value).padStart(2, '0');

const to12HourTimeParts = (value: string): { hour12: number; minute: number; meridiem: Meridiem } => {
  const match = /^(\d{1,2}):(\d{2})$/.exec(String(value ?? '').trim());
  if (!match) {
    return { hour12: 7, minute: 30, meridiem: 'AM' };
  }

  const hour24 = clamp(parseInt(match[1], 10), 0, 23);
  const minute = clamp(parseInt(match[2], 10), 0, 59);
  const meridiem: Meridiem = hour24 >= 12 ? 'PM' : 'AM';
  const hour12 = hour24 % 12 === 0 ? 12 : hour24 % 12;
  return { hour12, minute, meridiem };
};

const to24HourTime = (hour12: number, minute: number, meridiem: Meridiem): string => {
  const safeHour12 = clamp(hour12, 1, 12);
  const safeMinute = clamp(minute, 0, 59);
  const hour24 = meridiem === 'AM'
    ? (safeHour12 === 12 ? 0 : safeHour12)
    : (safeHour12 === 12 ? 12 : safeHour12 + 12);
  return `${pad2(hour24)}:${pad2(safeMinute)}`;
};

const EditMealSlotDialog: React.FC<EditMealSlotDialogProps> = (props) => {
  const {
    open,
    mode,
    isSaving,
    mealName,
    mealType,
    canEditMealType = false,
    mealTypeOptions = [],
    onMealTypeChange,
    mealTime,
    onMealNameChange,
    onMealTimeChange,
    onClose,
    onSave,
  } = props;

  const timeParts = React.useMemo(() => to12HourTimeParts(mealTime), [mealTime]);
  const hourInputRef = React.useRef<HTMLInputElement | null>(null);
  const minuteInputRef = React.useRef<HTMLInputElement | null>(null);
  const [hourDraft, setHourDraft] = React.useState(() => pad2(timeParts.hour12));
  const [minuteDraft, setMinuteDraft] = React.useState(() => pad2(timeParts.minute));

  const updateTime = React.useCallback(
    (nextHour12: number, nextMinute: number, nextMeridiem: Meridiem) => {
      onMealTimeChange(to24HourTime(nextHour12, nextMinute, nextMeridiem));
    },
    [onMealTimeChange],
  );

  React.useEffect(() => {
    setHourDraft(pad2(timeParts.hour12));
    setMinuteDraft(pad2(timeParts.minute));
  }, [timeParts.hour12, timeParts.minute]);

  const commitHourDraft = React.useCallback((value: string) => {
    const digits = value.replace(/\D/g, '').slice(0, 2);
    if (!digits) {
      setHourDraft(pad2(timeParts.hour12));
      return;
    }
    const parsedHour = clamp(parseInt(digits, 10), 1, 12);
    const nextHour = pad2(parsedHour);
    setHourDraft(nextHour);
    updateTime(parsedHour, timeParts.minute, timeParts.meridiem);
  }, [timeParts.hour12, timeParts.meridiem, timeParts.minute, updateTime]);

  const commitMinuteDraft = React.useCallback((value: string) => {
    const digits = value.replace(/\D/g, '').slice(0, 2);
    if (!digits) {
      setMinuteDraft(pad2(timeParts.minute));
      return;
    }
    const parsedMinute = clamp(parseInt(digits, 10), 0, 59);
    const nextMinute = pad2(parsedMinute);
    setMinuteDraft(nextMinute);
    updateTime(timeParts.hour12, parsedMinute, timeParts.meridiem);
  }, [timeParts.hour12, timeParts.meridiem, timeParts.minute, updateTime]);

  const handleHourChange = React.useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const digits = event.target.value.replace(/\D/g, '').slice(0, 2);
    setHourDraft(digits);
    if (digits.length === 2 || (digits.length === 1 && Number(digits) >= 2)) {
      commitHourDraft(digits);
      requestAnimationFrame(() => {
        minuteInputRef.current?.focus();
        minuteInputRef.current?.select();
      });
    }
  }, [commitHourDraft]);

  const handleMinuteChange = React.useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const digits = event.target.value.replace(/\D/g, '').slice(0, 2);
    setMinuteDraft(digits);
    if (digits.length === 2) {
      commitMinuteDraft(digits);
    }
  }, [commitMinuteDraft]);

  const handleMeridiemChange = React.useCallback(
    (event: SelectChangeEvent<Meridiem>) => {
      const nextMeridiem = String(event.target.value ?? 'AM').toUpperCase() === 'PM' ? 'PM' : 'AM';
      updateTime(timeParts.hour12, timeParts.minute, nextMeridiem);
    },
    [timeParts.hour12, timeParts.minute, updateTime],
  );

  const dialogTitle = mode === 'add' ? 'Add Meal Details' : 'Edit Meal Details';
  const normalizedMealType = React.useMemo(
    () => String(mealType ?? '').trim(),
    [mealType],
  );

  const resolvedMealTypeOptions = React.useMemo(() => {
    const optionMap = new Map<string, MealTypeOption>();
    mealTypeOptions.forEach((option) => {
      const value = String(option?.value ?? '').trim();
      if (!value) return;
      const label = String(option?.label ?? '').trim() || value;
      optionMap.set(value, { value, label });
    });
    if (normalizedMealType && !optionMap.has(normalizedMealType)) {
      optionMap.set(normalizedMealType, {
        value: normalizedMealType,
        label: normalizedMealType,
      });
    }
    return Array.from(optionMap.values());
  }, [mealTypeOptions, normalizedMealType]);

  const { mealTypeLabel, mealTypeIsNotice } = React.useMemo(() => {
    const normalizedMealType = String(mealType ?? '').trim();
    if (normalizedMealType) {
      return { mealTypeLabel: normalizedMealType, mealTypeIsNotice: false };
    }
    if (mode === 'add') {
      return {
        mealTypeLabel: mealName.trim() || 'Meal type will match meal name',
        mealTypeIsNotice: false,
      };
    }
    return { mealTypeLabel: 'Meal type is fixed for this slot', mealTypeIsNotice: true };
  }, [mealName, mealType, mode]);

  const fieldContainerSx = {
    position: 'relative',
    border: '2px solid #D4D8DE',
    borderRadius: '8px',
    bgcolor: '#ffffff',
    px: 1.5,
    py: 0.9,
    minHeight: 52,
  } as const;

  const fieldLabelSx = {
    position: 'absolute',
    top: -8,
    left: 13,
    px: 0.55,
    bgcolor: '#ffffff',
    fontFamily: 'Inter, sans-serif',
    fontSize: { xs: '0.75rem', md: '0.92rem' },
    fontWeight: 450,
    lineHeight: 1,
    color: '#5f6670',
  } as const;

  return (
    <Dialog
      open={open}
      onClose={() => {
        if (!isSaving) onClose();
      }}
      maxWidth={false}
      BackdropProps={{
        sx: {
          bgcolor: 'rgba(16, 24, 40, 0.68)',
        },
      }}
      PaperProps={{
        sx: {
          width: 'min(600px, calc(100% - 48px))',
          maxWidth: '600px',
          minHeight: { xs: 318, md: 334 },
          borderRadius: '14px',
          m: 0,
          overflow: 'visible',
        },
      }}
    >
      <Box sx={{ px: { xs: 2, md: 3 }, py: { xs: 2.2, md: 3.2 } }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, alignItems: 'flex-start' }}>
          <Box>
            <Typography
              sx={{
                fontFamily: 'Inter, sans-serif',
                fontSize: { xs: '0.81rem', md: '1.23rem' },
                fontWeight: 600,
                lineHeight: 1.15,
                color: '#1f2430',
              }}
            >
              {dialogTitle}
            </Typography>
            <Typography
              sx={{
                mt: 0.9,
                fontFamily: 'Inter, sans-serif',
                fontSize: { xs: '0.75rem', md: '0.77rem' },
                fontWeight: 400,
                lineHeight: 1.3,
                color: '#3f4650',
              }}
            >
              Configure the meal details below
            </Typography>
          </Box>
          <IconButton
            onClick={onClose}
            disabled={isSaving}
            sx={{
              width: { xs: 34, md: 42 },
              height: { xs: 34, md: 42 },
              bgcolor: '#E7EDF3',
              color: '#121826',
              '&:hover': { bgcolor: '#dbe5ef' },
              '&.Mui-disabled': { bgcolor: '#eff3f7', color: '#98A2B3' },
            }}
          >
            <CloseRoundedIcon sx={{ fontSize: { xs: 13, md: 16 } }} />
          </IconButton>
        </Box>

        <Box
          sx={{
            mt: { xs: 1.8, md: 3.3 },
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
            gap: { xs: 1.8, md: 2.8 },
          }}
        >
          <Box sx={{ ...fieldContainerSx, display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }}>
            <Typography sx={{ ...fieldLabelSx, fontWeight: 400 }}>Meal Name</Typography>
            <InputBase
              value={mealName}
              onChange={(event) => onMealNameChange(event.target.value)}
              placeholder="Enter meal name"
              disabled={isSaving}
              sx={{
                width: '100%',
                '& .MuiInputBase-input': {
                  p: 0,
                  fontFamily: 'Inter, sans-serif',
                  fontSize: { xs: '0.75rem', md: '1.02rem' },
                  fontWeight: 500,
                  lineHeight: 1.2,
                  color: '#111827',
                  textAlign: 'left',
                },
              }}
            />
          </Box>

          <Box sx={fieldContainerSx}>
            <Typography sx={fieldLabelSx}>Select time</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', gap: 0.9, width: '100%' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.65 }}>
                <InputBase
                  value={hourDraft}
                  inputRef={hourInputRef}
                  onChange={handleHourChange}
                  onFocus={(event) => event.currentTarget.select()}
                  onBlur={() => commitHourDraft(hourDraft)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      event.preventDefault();
                      commitHourDraft(hourDraft);
                      minuteInputRef.current?.focus();
                      minuteInputRef.current?.select();
                      return;
                    }
                    if (event.key === 'ArrowUp') {
                      event.preventDefault();
                      const nextHour = timeParts.hour12 === 12 ? 1 : timeParts.hour12 + 1;
                      const nextHourText = pad2(nextHour);
                      setHourDraft(nextHourText);
                      updateTime(nextHour, timeParts.minute, timeParts.meridiem);
                      return;
                    }
                    if (event.key === 'ArrowDown') {
                      event.preventDefault();
                      const nextHour = timeParts.hour12 === 1 ? 12 : timeParts.hour12 - 1;
                      const nextHourText = pad2(nextHour);
                      setHourDraft(nextHourText);
                      updateTime(nextHour, timeParts.minute, timeParts.meridiem);
                    }
                  }}
                  disabled={isSaving}
                  inputProps={{ inputMode: 'numeric', maxLength: 2 }}
                  sx={{
                    width: { xs: 34, md: 40 },
                    '& .MuiInputBase-input': {
                      p: 0,
                      fontFamily: 'Inter, sans-serif',
                      fontSize: { xs: '0.75rem', md: '0.98rem' },
                      fontWeight: 500,
                      lineHeight: 1.2,
                      color: '#111827',
                      textAlign: 'center',
                    },
                  }}
                />
                <Typography
                  sx={{
                    fontFamily: 'Inter, sans-serif',
                    fontSize: { xs: '0.75rem', md: '0.92rem' },
                    fontWeight: 500,
                    color: '#111827',
                    lineHeight: 1,
                  }}
                >
                  :
                </Typography>
                <InputBase
                  value={minuteDraft}
                  inputRef={minuteInputRef}
                  onChange={handleMinuteChange}
                  onFocus={(event) => event.currentTarget.select()}
                  onBlur={() => commitMinuteDraft(minuteDraft)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      event.preventDefault();
                      commitMinuteDraft(minuteDraft);
                      return;
                    }
                    if (event.key === 'ArrowUp') {
                      event.preventDefault();
                      const nextMinute = (timeParts.minute + 1) % 60;
                      const nextMinuteText = pad2(nextMinute);
                      setMinuteDraft(nextMinuteText);
                      updateTime(timeParts.hour12, nextMinute, timeParts.meridiem);
                      return;
                    }
                    if (event.key === 'ArrowDown') {
                      event.preventDefault();
                      const nextMinute = (timeParts.minute + 59) % 60;
                      const nextMinuteText = pad2(nextMinute);
                      setMinuteDraft(nextMinuteText);
                      updateTime(timeParts.hour12, nextMinute, timeParts.meridiem);
                    }
                  }}
                  disabled={isSaving}
                  inputProps={{ inputMode: 'numeric', maxLength: 2 }}
                  sx={{
                    width: { xs: 34, md: 40 },
                    '& .MuiInputBase-input': {
                      p: 0,
                      fontFamily: 'Inter, sans-serif',
                      fontSize: { xs: '0.75rem', md: '0.98rem' },
                      fontWeight: 500,
                      lineHeight: 1.2,
                      color: '#111827',
                      textAlign: 'center',
                    },
                  }}
                />
              </Box>

              <Select
                value={timeParts.meridiem}
                onChange={handleMeridiemChange}
                disabled={isSaving}
                variant="standard"
                disableUnderline
                IconComponent={KeyboardArrowDownRoundedIcon}
                sx={{
                  ml: 'auto',
                  width: { xs: 53, md: 72 },
                  height: { xs: 31, md: 43 },
                  px: { xs: 0.9, md: 1.3 },
                  borderRadius: '6px',
                  bgcolor: '#59ADD5',
                  color: '#ffffff',
                  '& .MuiSelect-select': {
                    p: 0,
                    fontFamily: 'Inter, sans-serif',
                    fontSize: { xs: '0.75rem', md: '0.85rem' },
                    fontWeight: 500,
                    lineHeight: 1,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.45,
                  },
                  '& .MuiSelect-icon': {
                    color: '#ffffff',
                    right: 5,
                    fontSize: { xs: 14, md: 17 },
                  },
                  '&.Mui-disabled': {
                    bgcolor: '#b7d8e7',
                    color: '#ffffff',
                  },
                }}
                MenuProps={{
                  disablePortal: false,
                  keepMounted: true,
                  anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
                  transformOrigin: { vertical: 'top', horizontal: 'left' },
                  PaperProps: {
                    sx: {
                      mt: 0.6,
                      borderRadius: '10px',
                      border: '1px solid #d4dbe5',
                      boxShadow: '0 10px 24px rgba(15, 23, 42, 0.16)',
                    },
                  },
                }}
              >
                <MenuItem value="AM">AM</MenuItem>
                <MenuItem value="PM">PM</MenuItem>
              </Select>
            </Box>
          </Box>
        </Box>

        <Box sx={{ ...fieldContainerSx, mt: { xs: 1.5, md: 1.8 }, minHeight: 52, display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }}>
          <Typography sx={{ ...fieldLabelSx, fontWeight: 400 }}>Meal Type</Typography>
          {canEditMealType ? (
            <Select
              value={normalizedMealType}
              onChange={(event) => onMealTypeChange?.(String(event.target.value ?? '').trim())}
              disabled={isSaving}
              size="small"
              IconComponent={KeyboardArrowDownRoundedIcon}
              sx={{
                width: '100%',
                minHeight: 28,
                '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                '& .MuiSelect-select': {
                  p: 0,
                  fontFamily: 'Inter, sans-serif',
                  fontSize: { xs: '0.75rem', md: '1.02rem' },
                  fontWeight: 500,
                  lineHeight: 1.2,
                  color: '#111827',
                  textAlign: 'left',
                  pr: '20px !important',
                },
                '& .MuiSelect-icon': {
                  color: '#6b7280',
                  right: 0,
                },
              }}
            >
              {resolvedMealTypeOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          ) : (
            <Typography
              sx={{
                fontFamily: 'Inter, sans-serif',
                fontSize: { xs: '0.75rem', md: '1.02rem' },
                fontWeight: 500,
                lineHeight: 1.2,
                color: mealTypeIsNotice ? '#98A2B3' : '#111827',
                textAlign: 'left',
              }}
            >
              {mealTypeLabel}
            </Typography>
          )}
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'center', mt: { xs: 2.4, md: 3.6 } }}>
          <Button
            variant="contained"
            onClick={onSave}
            disabled={isSaving}
            sx={{
              width: { xs: '100%', sm: 252 },
              maxWidth: '100%',
              height: { xs: 35, md: 52 },
              borderRadius: '10px',
              bgcolor: '#59ADD5',
              color: '#ffffff',
              textTransform: 'none',
              fontFamily: 'Inter, sans-serif',
              fontSize: { xs: '0.75rem', md: '0.94rem' },
              fontWeight: 700,
              '&:hover': { bgcolor: '#3f9fc9' },
              '&.Mui-disabled': { bgcolor: '#b8dcea', color: '#ffffff' },
            }}
          >
            {isSaving ? 'Saving...' : 'Save Changes'}
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
};

export default EditMealSlotDialog;
